import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import PropTypes from "prop-types";
import Moment from "react-moment";

import IconButton from "@material-ui/core/IconButton";

import { DataGrid, getGridSingleSelectOperators } from "@mui/x-data-grid";

import VisibilityIcon from "@material-ui/icons/Visibility";

import QuickSearchToolbar from "./QuickSearchToolbar";
import { useDispatch, useSelector } from "react-redux";
import { setPage, setRowsPerPage } from "../../redux/actions/datagridActions";
import api from "../../api/api";

const Badge = styled.span`
  font-size: 11px;
  background-color: ${(props) => props.color};
  color: white;
  border-radius: 8px;
  padding: 4px 8px;
  text-align: center;
`;

QuickSearchToolbar.propTypes = {
  clearSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
};

export default function SupportTicketTable({ tickets }) {
  const defaultStatusFilterValue = 2;
  const defaultStatusFilterOperator = "not";
  const statusOptions = [
    { label: "Open", value: 1 },
    { label: "Closed", value: 2 },
    { label: "Awaiting Response", value: 3 },
    { label: "Resolved & Awaiting Confirmation", value: 4 },
  ];

  const dispatch = useDispatch();

  const history = useHistory();
  const auth = useSelector((state) => state.authReducer);
  const gridState = useSelector((state) => state.datagridReducer);

  const [searchText, setSearchText] = useState("");
  const [filterValue, setFilterValue] = useState(defaultStatusFilterValue);
  const [filterOperator, setFilterOperator] = useState(
    defaultStatusFilterOperator
  );
  const [rows, setRows] = useState(tickets);
  const [filt, setFilt] = useState([
    {
      columnField: "supportTicketStatusId",
      operatorValue: defaultStatusFilterOperator,
      value: defaultStatusFilterValue,
    },
  ]);

  const onFilterChange = React.useCallback((filterModel) => {
    const selectedFilter = filterModel?.items?.[0];
    if (!selectedFilter) {
      setFilterValue("");
      return;
    }

    const selectedOperator = selectedFilter.operatorValue;
    if (selectedOperator) {
      setFilterOperator(selectedOperator);
    }

    const selectedValue = selectedFilter.value;
    if (
      selectedValue === undefined ||
      selectedValue === null ||
      selectedValue === ""
    ) {
      setFilterValue("");
      return;
    }

    const numericValue = Number(selectedValue);
    if (!Number.isNaN(numericValue)) {
      setFilterValue(numericValue);
      return;
    }

    const selectedOption = statusOptions.find(
      (option) => option.label === selectedValue
    );
    if (selectedOption) {
      setFilterValue(selectedOption.value);
    }
  }, []);

  const [sortModel, setSortModel] = useState([
    {
      field: "createdDate",
      sort: "desc",
    },
  ]);

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handlePageSizeChange = (size) => {
    dispatch(setRowsPerPage(size));
  };

  const statusFilterQuery =
    filterValue === ""
      ? ""
      : `SupportTicketStatusId=${filterValue}&SupportTicketStatusOperator=${filterOperator}&`;

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
    api
      .get(
        `api/SupportTicket?${statusFilterQuery}Keyword=${searchValue}&PageSize=${
          gridState?.size
        }&PageNumber=${gridState?.page + 1}&v=1.0`,
        {
          headers: {
            Authorization: `Bearer ${auth.user?.token}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.isSuccess) {
          setRows(response.data.data);
        }
      })
      .catch((error) => {
        console.log(error.message);
      });
  };

  useEffect(() => {
    setRows(tickets);
  }, [tickets]);

  useEffect(() => {
    let active = true;
    api
      .get(
        `api/SupportTicket?${statusFilterQuery}Keyword=${searchText}&PageSize=${
          gridState?.size
        }&PageNumber=${gridState?.page + 1}&v=1.0`,
        {
          headers: {
            Authorization: `Bearer ${auth.user?.token}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.isSuccess) {
          if (!active) {
            return;
          }
          if (filterValue === "") {
            setFilt([]);
          } else {
            setFilt([
              {
                columnField: "supportTicketStatusId",
                operatorValue: filterOperator,
                value: filterValue,
              },
            ]);
          }
          setRows(response.data.data);
        }
      })
      .catch((error) => {
        console.log(error.message);
      });
    return () => {
      active = false;
    };
  }, [filterValue, filterOperator]);

  const cols = [
    { field: "id", headerName: "ID", width: 80, filterable: false },
    {
      field: "fullName",
      headerName: "RequestedBy",
      flex: 1,
      minWidth: 100,
      filterable: false,
    },
    {
      field: "title",
      headerName: "Subject",
      flex: 1,
      minWidth: 100,
      filterable: false,
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      flex: 1,
      minWidth: 100,
      filterable: false,
      renderCell: (params) => (
        <Moment date={params.value} format="DD/MM/YYYY HH:mm:ss" />
      ),
    },
    {
      field: "replyDate",
      headerName: "Reply Date",
      type: "dateTime",
      flex: 1,
      minWidth: 100,
      filterable: false,
      valueGetter: (params) => {
        if (!params?.row?.replyDateText) {
          return null;
        }
        return new Date(params.row.replyDate);
      },
      renderCell: (params) =>
        params?.row?.replyDateText ? (
          <Moment date={params.row.replyDate} format="DD/MM/YYYY HH:mm:ss" />
        ) : (
          ""
        ),
    },
    {
      field: "replyName",
      headerName: "Reply Name",
      flex: 1,
      minWidth: 100,
      filterable: false,
    },

    {
      field: "supportTicketStatusId",
      headerName: "Status",
      width: 230,
      align: "center",
      valueOptions: statusOptions,
      filterOperators: getGridSingleSelectOperators().filter(
        (operator) => operator.value === "is" || operator.value === "not"
      ),
      renderCell: (params) => {
        const text =
          params?.row?.supportTicketStatusName ||
          statusOptions.find((option) => option.value === params.value)
            ?.label ||
          "";

        let color = "#9c9c9c";
        if (params.value === 1) {
          color = "#22b9e3";
        } else if (params.value === 2) {
          color = "#9c9c9c";
        } else if (params.value === 3) {
          color = "#f39c12";
        } else if (params.value === 4) {
          color = "#6c5ce7";
        }

        return <Badge color={color}>{text}</Badge>;
      },
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      width: 100,
      align: "center",
      filterable: false,

      renderCell: (params) => {
        const onClick = () => {
          history.push(`/supportticketdetail/${params.row.id}`);
        };

        return (
          <IconButton onClick={onClick}>
            <VisibilityIcon />
          </IconButton>
        );
      },
    },
  ];

  if (
    [
      "BromcomSuperUser",
      "BromcomAdmin",
      "BromcomDeveloper",
      "BromcomSupport",
    ].includes(auth?.user?.rol)
  ) {
    cols.splice(4, 0, {
      field: "companyName",
      headerName: "Company Name",
      flex: 1,
      minWidth: 100,
      filterable: false,
    });

    cols.splice(7, 0, {
      field: "assignedUserName",
      headerName: "Assigned To",
      flex: 1,
      minWidth: 100,
      filterable: false,
    });
  }

  return (
    <div style={{ height: 760, width: "100%" }}>
      <DataGrid
        sx={{
          "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },

          "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
        components={{ Toolbar: QuickSearchToolbar }}
        rows={rows}
        columns={cols}
        sortModel={sortModel}
        rowsPerPageOptions={[10, 50, 100]}
        componentsProps={{
          toolbar: {
            value: searchText,
            onChange: (event) => requestSearch(event.target.value),
            clearSearch: () => requestSearch(" "),
          },
        }}
        filterModel={{ items: filt }}
        onFilterModelChange={onFilterChange}
        filterMode="server"
        onSortModelChange={(model) => setSortModel(model)}
        disableSelectionOnClick
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        scrollbarSize={30}
        onRowDoubleClick={(e) => {
          history.push(`/supportticketdetail/${e.row.id}`);
        }}
        paginationMode="server"
        rowCount={gridState.totalCount}
        loading={gridState.isGridLoading}
      />
    </div>
  );
}
